// Coste interno de personal (Logística 2, nivel 2), siempre visible: alguien
// de Duetto frente a un extra (horas y coste; un socio, sin coste), camareros
// y extras de fuera, se queda o vuelve a recoger, por jornada en multievento y
// el total del personal del evento. Debajo, el estado del guardado de Quién va.
// Mismos cálculos que la pestaña Logística (utils/quienVaEvento.js).
// Si hay orden de pedido de personal extra vigente con personas (no anulada ni
// cancelada, también en borrador), arriba va su coste: es el que toma el
// presupuesto (motor de costes) y la estimación de Quién va queda como
// referencia. Mismo cálculo que la pestaña de la orden (utils/ordenPersonalExtra.js).
import React from 'react';
import { QUIEN_RECOGE, horaConDia } from '../../../utils/quienVaEvento';
import { resumenOrdenPersonal } from '../../../utils/ordenPersonalExtra';
import { eurosCorto, formatEuros, numeroEs, textoGuardadoPor } from '../../../utils/logistica2';
import { botonPrincipal } from './estilos';
import useEsMovil from '../../../hooks/useEsMovil';

const formatHoras = (h) => numeroEs(h, 2);
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
const claseTd = 'px-2 py-1.5 text-xs border-b border-[#EEF1EF] align-top';

// `jornadaEtiqueta`: con una jornada elegida (multievento), `qv` es el de esa
// jornada y la tabla es la suya. `presupuesto` (y `jornada` en multievento):
// para leer la orden de pedido de personal extra.
const CostesPersonal = ({ qv, auto = false, jornadaEtiqueta = null, presupuesto = null, jornada = null }) => {
  // Móvil (< 768 px): el coste por jornada va en tarjetas en vez de en tabla
  const esMovil = useEsMovil();
  const {
    calculo, comparativa, tarifas, modo, sinCostePersona, multi, jornadas, seVa, tipoRecogida, datos,
    aviso, guardando, hayCambios, faltaParaGuardar, modoGuardado, guardar,
  } = qv || {};
  if (!calculo) return null;
  const etiquetaRecogida = (quien) => (QUIEN_RECOGE.find(q => q.clave === quien) || {}).etiqueta || '';
  const detalleOpcion = (clave, tarifa) => {
    if (clave === 'propio' && sinCostePersona) {
      return calculo.recogida?.aparte ? `Montaje sin coste (socio) + recogida (${etiquetaRecogida(calculo.recogida.quien)})` : `${formatHoras(calculo.horas)} h · sin coste (socio)`;
    }
    return calculo.recogida?.aparte
      ? `Montaje a ${formatEuros(tarifa)}/h + recogida (${etiquetaRecogida(calculo.recogida.quien)})`
      : `${formatHoras(calculo.horas)} h × ${formatEuros(tarifa)}/h`;
  };
  let textoDiferencia;
  if (calculo.masBarato === 'igual') textoDiferencia = 'Cuesta lo mismo con alguien de Duetto que con un extra.';
  else if (calculo.masBarato === 'propio') textoDiferencia = `Un extra sale ${eurosCorto(Math.abs(calculo.diferencia))} más caro.`;
  else textoDiferencia = `Un extra sale ${eurosCorto(Math.abs(calculo.diferencia))} más barato.`;
  const totalPersonal = calculo.total ?? calculo.totalConPropio;
  const modoTabla = modo || 'propio';
  // Orden de pedido vigente con personas: manda en el coste del presupuesto
  const orden = presupuesto ? resumenOrdenPersonal(presupuesto, jornada) : null;
  const diferenciaOrden = orden ? Math.round((orden.coste - (Number(totalPersonal) || 0)) * 100) / 100 : 0;

  return (
    <div className="flex flex-col gap-2 text-[#5B6B64]" data-testid="l2-costes-personal">
      {orden && (
        <div className="rounded-md border border-[#BFE3D2] bg-[#E3F3EC] px-3 py-2 text-xs text-[#0F5E43]" data-testid="l2-costes-orden">
          <p className="text-sm font-semibold text-[#16221D]" data-testid="l2-costes-orden-total">
            Según la orden de pedido: {plural(orden.personas, 'persona', 'personas')} · {formatHoras(orden.horas)} h · {formatEuros(orden.coste)}
          </p>
          <p data-testid="l2-costes-orden-estado">Orden {orden.textoEstado}.</p>
          <p>El coste del presupuesto toma la orden; la estimación de Quién va queda como referencia.</p>
          {Math.abs(diferenciaOrden) >= 0.005 && (
            <p className="font-semibold" data-testid="l2-costes-orden-diferencia">
              {diferenciaOrden > 0 ? '+' : '-'}{formatEuros(Math.abs(diferenciaOrden))} respecto a la estimación
            </p>
          )}
        </div>
      )}
      {orden && <p className="text-xs md:text-[11px] font-semibold uppercase tracking-wide text-[#5B6B64]" data-testid="l2-costes-estimacion-rotulo">Estimación de Quién va</p>}
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-xs md:text-[11px] text-[#5B6B64]">
            <th className="px-2 py-1 text-left font-semibold">Responsable del montaje</th>
            <th className="px-2 py-1 text-right font-semibold">Coste</th>
          </tr>
        </thead>
        <tbody>
          <tr className={modo === 'propio' ? 'bg-[#E3F3EC]' : ''}>
            <td className={claseTd}>
              Alguien de Duetto
              <span className="block text-xs md:text-[11px]" data-testid="l2-costes-propio-detalle">{detalleOpcion('propio', tarifas.propio)}</span>
            </td>
            <td className={`${claseTd} text-right font-semibold tabular-nums text-[#33443C]`} data-testid="l2-costes-propio">
              {sinCostePersona && !calculo.recogida?.aparte ? 'sin coste' : formatEuros(calculo.costePropio)}
            </td>
          </tr>
          <tr className={modo === 'extra' ? 'bg-[#E3F3EC]' : ''}>
            <td className={claseTd}>
              Extra externo
              <span className="block text-xs md:text-[11px]">{detalleOpcion('extra', tarifas.extra)}</span>
            </td>
            <td className={`${claseTd} text-right font-semibold tabular-nums text-[#33443C]`} data-testid="l2-costes-extra">{formatEuros(calculo.costeExtra)}</td>
          </tr>
          <tr>
            <td className={claseTd}>
              Camareros y extras de fuera
              <span className="block text-xs md:text-[11px]">
                {calculo.personalMenu.personas === 0
                  ? 'Ninguno'
                  : `${plural(calculo.personalMenu.personas, multi ? 'persona-jornada' : 'persona', multi ? 'personas-jornada' : 'personas')} · ${formatHoras(calculo.personalMenu.horasPersona)} h × ${formatEuros(tarifas.extra)}/h`}
              </span>
            </td>
            <td className={`${claseTd} text-right font-semibold tabular-nums text-[#33443C]`}>{formatEuros(calculo.personalMenu.coste)}</td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs" data-testid="l2-costes-diferencia">
        Jornada de {formatHoras(calculo.horas)} h{multi ? ` en ${jornadas.length} jornadas` : ''}. {textoDiferencia}
      </p>
      {(multi || jornadaEtiqueta) && (
        <p className="text-xs text-[#0F5E43] bg-[#E3F3EC] rounded-md px-2 py-1.5" data-testid="l2-costes-por-jornada">
          El coste del personal propio se calcula por jornada, según quién va cada día; no hay un único responsable de montaje para todo el multievento.
          {jornadaEtiqueta ? ` Aquí, solo la ${jornadaEtiqueta}.` : ''}
        </p>
      )}

      {multi && esMovil && (
        <ul className="flex flex-col gap-2" data-testid="l2-costes-jornadas-movil">
          {jornadas.map(({ j, est, modo: modoJ, personaNombre: nombreJ }) => {
            const c = est.calculo;
            const h = est.horario;
            const modoFila = modoJ || modo;
            const costeResp = modoFila === 'extra' ? c.costeExtra : c.costePropio;
            let quien = 'Sin elegir';
            if (modoFila === 'extra') quien = 'Un extra';
            else if (modoFila === 'propio') quien = nombreJ || 'Equipo propio';
            const textoHorario = h ? (h.bloques || []).map(b => `${horaConDia(b.inicio, b.dia_inicio)} a ${horaConDia(b.fin, b.dia_fin)}`).join(' y ') : '-';
            return (
              <li key={j.id} className="rounded-lg border border-[#E1E6E3] bg-white px-3 py-2 text-[13px] flex flex-col gap-0.5">
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0 font-semibold text-[#16221D]">{j.etiqueta}</span>
                  <strong className="tabular-nums text-[#16221D] whitespace-nowrap">{formatEuros(costeResp + c.personalMenu.coste)}</strong>
                </div>
                <span className="tabular-nums">{textoHorario}</span>
                <span className="tabular-nums">Quién va: {quien} · {formatHoras(c.horas)} h · {formatEuros(costeResp)}</span>
                <span className="tabular-nums">De fuera: {c.personalMenu.personas} · {formatEuros(c.personalMenu.coste)}</span>
              </li>
            );
          })}
        </ul>
      )}
      {multi && !esMovil && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border border-[#E1E6E3] rounded-lg">
            <thead>
              <tr className="bg-[#F7F9F8]">
                <th className="px-2 py-1.5 text-left font-semibold">Jornada</th>
                <th className="px-2 py-1.5 text-left font-semibold">Horario</th>
                <th className="px-2 py-1.5 text-right font-semibold">Quién va (responsable)</th>
                <th className="px-2 py-1.5 text-right font-semibold">De fuera</th>
                <th className="px-2 py-1.5 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EEF1EF]">
              {jornadas.map(({ j, est, modo: modoJ, personaNombre: nombreJ }) => {
                const c = est.calculo;
                const h = est.horario;
                // Cada jornada con su quién va (el suyo o el del evento)
                const modoFila = modoJ || modo;
                const costeResp = modoFila === 'extra' ? c.costeExtra : c.costePropio;
                let quien = 'Sin elegir';
                if (modoFila === 'extra') quien = 'Un extra';
                else if (modoFila === 'propio') quien = nombreJ || 'Equipo propio';
                const textoHorario = h ? (h.bloques || []).map(b => `${horaConDia(b.inicio, b.dia_inicio)} a ${horaConDia(b.fin, b.dia_fin)}`).join(' y ') : '-';
                return (
                  <tr key={j.id}>
                    <td className="px-2 py-1.5">{j.etiqueta}</td>
                    <td className="px-2 py-1.5">{textoHorario}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      <span className="block text-xs md:text-[11px] text-[#5B6B64]">{quien}</span>
                      {formatHoras(c.horas)} h · {formatEuros(costeResp)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{c.personalMenu.personas} · {formatEuros(c.personalMenu.coste)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{formatEuros(costeResp + c.personalMenu.coste)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {comparativa && (
        <div className="overflow-x-auto" data-testid="l2-costes-comparativa">
          <table className="w-full text-xs border border-[#E1E6E3] rounded-lg">
            <thead>
              <tr className="bg-[#F7F9F8]">
                <th className="px-2 py-1.5 text-left font-semibold">Durante el evento</th>
                <th className="px-2 py-1.5 text-right font-semibold">Alguien de Duetto</th>
                <th className="px-2 py-1.5 text-right font-semibold">Extra externo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EEF1EF]">
              {[['se_queda', 'Se queda todo el evento'], ['se_va', tipoRecogida === 'otro_dia' ? 'Recoge otro día' : 'Vuelve luego a recoger']].map(([fila, etiqueta]) => (
                <tr key={fila}>
                  <td className="px-2 py-1.5">{etiqueta}</td>
                  {['propio', 'extra'].map(col => {
                    const elegida = (fila === 'se_va') === !!seVa && col === modoTabla;
                    return (
                      <td key={col} className={`px-2 py-1.5 text-right tabular-nums ${elegida ? 'bg-[#E3F3EC] font-bold text-[#0F5E43]' : ''}`}
                        data-testid={`l2-costes-comparativa-${fila}-${col}`}>
                        {formatHoras(comparativa[fila].horas)} h · {formatEuros(comparativa[fila][col])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 text-xs md:text-[11px]">Con el combustible de sus viajes. Resaltada, la opción elegida: es la que entra en el coste.</p>
        </div>
      )}

      <p className={orden ? 'text-xs font-semibold' : 'text-sm font-semibold text-[#16221D]'} data-testid="l2-costes-total-personal">
        {orden ? 'Estimación de Quién va' : 'Coste total del personal del evento'}: {formatEuros(totalPersonal)}
      </p>
      {orden && (
        <p className="text-sm font-semibold text-[#16221D]" data-testid="l2-costes-total-orden">
          Coste del personal del evento (orden de pedido): {formatEuros(orden.coste)}
        </p>
      )}
      <p className="text-xs md:text-[11px]">Solo para nosotros: no se cobra al cliente.</p>

      {/* Estado del guardado de Quién va */}
      <div className="flex flex-col gap-1 text-xs">
        {!auto && (
          <button type="button" onClick={() => guardar()} disabled={guardando || (!hayCambios && !!modoGuardado)}
            className={`self-start ${botonPrincipal}`} data-testid="l2-guardar">
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        )}
        {auto && guardando && <span data-testid="l2-guardando">Guardando...</span>}
        {hayCambios && faltaParaGuardar && <p className="text-[#92400E]" data-testid="l2-falta-para-guardar">{faltaParaGuardar}</p>}
        {!modoGuardado && !hayCambios && <p>Todavía no se ha decidido quién va.</p>}
        {aviso && <p className={aviso.tipo === 'ok' ? 'text-[#0F5E43]' : 'text-[#8A1C12]'} data-testid="l2-quien-va-mensaje">{aviso.texto}</p>}
        {datos?.guardado?.actualizado_en && (
          <p className="text-[#8A9892]" data-testid="l2-guardado-por">{textoGuardadoPor(datos.guardado.actualizado_por, datos.guardado.actualizado_en)}</p>
        )}
      </div>
    </div>
  );
};

export default CostesPersonal;
